import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createAssistant,
  deleteAssistant,
  fetchAssistantById,
  fetchAssistants,
  fetchDoctorRendezvousAsAssistant,
  loginAssistant,
  resendAssistantInvitation,
  setAssistantActivationPassword,
  updateAssistant,
  verifyAssistantActivationToken,
} from "../redux/thunks/assistantManagement.thunks";
import {
  selectAssistantActivationState,
  selectAssistantAuthState,
  selectAssistantDoctorRendezvous,
  selectAssistantDoctorRendezvousError,
  selectAssistantDoctorRendezvousLoading,
  selectAssistantListError,
  selectAssistantListLoading,
  selectAssistantMutationError,
  selectAssistantMutationLoading,
  selectAssistants,
} from "../redux/selectors/assistantManagement.selectors";

export default function useAssistantManagement() {
  const dispatch = useDispatch();

  const assistants = useSelector(selectAssistants);
  const listLoading = useSelector(selectAssistantListLoading);
  const listError = useSelector(selectAssistantListError);

  const mutationLoading = useSelector(selectAssistantMutationLoading);
  const mutationError = useSelector(selectAssistantMutationError);

  const activation = useSelector(selectAssistantActivationState);
  const auth = useSelector(selectAssistantAuthState);

  const doctorRendezvous = useSelector(selectAssistantDoctorRendezvous);
  const doctorRendezvousLoading = useSelector(selectAssistantDoctorRendezvousLoading);
  const doctorRendezvousError = useSelector(selectAssistantDoctorRendezvousError);

  // Le backend identifie déjà le médecin via x-doctor-id.
  const loadAssistants = useCallback(
    (params = {}) => dispatch(fetchAssistants(params)),
    [dispatch]
  );

  const loadActiveAssistants = useCallback(
    () => dispatch(fetchAssistants({ status: "active" })),
    [dispatch]
  );

  const searchAssistants = useCallback(
    (search) => dispatch(fetchAssistants({ search })),
    [dispatch]
  );

  const filterAndSearchAssistants = useCallback(
    ({ status, search }) => dispatch(fetchAssistants({ status, search })),
    [dispatch]
  );

  const loadAssistantById = useCallback(
    (assistantId) => dispatch(fetchAssistantById(assistantId)),
    [dispatch]
  );

  const createAssistantEntry = useCallback(
    (payload) => dispatch(createAssistant(payload)),
    [dispatch]
  );

  const updateAssistantEntry = useCallback(
    (assistantId, payload) => dispatch(updateAssistant({ assistantId, payload })),
    [dispatch]
  );

  const resendInvitation = useCallback(
    (assistantId) => dispatch(resendAssistantInvitation(assistantId)),
    [dispatch]
  );

  const removeAssistant = useCallback(
    (assistantId) => dispatch(deleteAssistant(assistantId)),
    [dispatch]
  );

  const verifyActivationToken = useCallback(
    (token) => dispatch(verifyAssistantActivationToken(token)),
    [dispatch]
  );

  const setActivationPassword = useCallback(
    ({ token, password, confirmPassword }) =>
      dispatch(setAssistantActivationPassword({ token, password, confirmPassword })),
    [dispatch]
  );

  const login = useCallback(
    ({ email, password }) => dispatch(loginAssistant({ email, password })),
    [dispatch]
  );

  const loadDoctorRendezvous = useCallback(
    (doctorId) => dispatch(fetchDoctorRendezvousAsAssistant(doctorId)),
    [dispatch]
  );

  return {
    assistants,
    listLoading,
    listError,
    mutationLoading,
    mutationError,
    activation,
    auth,
    doctorRendezvous,
    doctorRendezvousLoading,
    doctorRendezvousError,

    loadAssistants,
    loadActiveAssistants,
    searchAssistants,
    filterAndSearchAssistants,
    loadAssistantById,
    createAssistantEntry,
    updateAssistantEntry,
    resendInvitation,
    removeAssistant,
    verifyActivationToken,
    setActivationPassword,
    login,
    loadDoctorRendezvous,
  };
}